// Libellés standards de l'état des résultats GS Gestion.
// Utilisés à la fois par le seed et par la page de saisie pour pré-créer
// les lignes vides quand une nouvelle période est ouverte.
//
// Le modèle reflète les lignes ATOMIQUES du relevé mensuel consolidé (pas les
// sous-totaux) : la somme des dépenses réconcilie au Net Income réel. Chaque
// dépense porte un `group` pour le regroupement visuel (couleurs, répartition).

export type FinanceSection = 'revenue' | 'expense'

// Groupes de dépenses (ordre d'affichage = ordre de déclaration).
export type ExpenseGroup = 'cogs' | 'indirect_cogs' | 'opex' | 'other'

export const EXPENSE_GROUP_LABELS: Record<ExpenseGroup, string> = {
  cogs: 'Coût des ventes',
  indirect_cogs: 'COGS indirects',
  opex: 'Frais indirects',
  other: 'Autres charges',
}

export type FinanceCatalogEntry = {
  section: FinanceSection
  code: string
  label: string
  sort_order: number
  group?: ExpenseGroup
}

export const FINANCE_CATALOG: readonly FinanceCatalogEntry[] = [
  // ─── Revenus par département d'affaires ─────────────────────────────────
  { section: 'revenue', code: 'TCP',    label: 'TCP',           sort_order: 10 },
  { section: 'revenue', code: 'LCT',    label: 'LCT',           sort_order: 20 },
  { section: 'revenue', code: 'RENTAL', label: 'Rental',        sort_order: 30 },
  { section: 'revenue', code: 'SALES',  label: 'Sales',         sort_order: 40 },
  { section: 'revenue', code: 'TMP',    label: 'TMP',           sort_order: 50 },
  { section: 'revenue', code: 'OTHER',  label: 'Other Revenue', sort_order: 60 },

  // ─── Coût des ventes direct (par département) ───────────────────────────
  { section: 'expense', code: 'COGS_TCP',    label: 'Coût des ventes — TCP',    sort_order: 110, group: 'cogs' },
  { section: 'expense', code: 'COGS_LCT',    label: 'Coût des ventes — LCT',    sort_order: 120, group: 'cogs' },
  { section: 'expense', code: 'COGS_RENTAL', label: 'Coût des ventes — Rental', sort_order: 130, group: 'cogs' },
  { section: 'expense', code: 'COGS_SALES',  label: 'Coût des ventes — Sales',  sort_order: 140, group: 'cogs' },
  { section: 'expense', code: 'COGS_TMP',    label: 'Coût des ventes — TMP',    sort_order: 150, group: 'cogs' },
  { section: 'expense', code: 'COGS_OTHER',  label: 'Coût des ventes — Other',  sort_order: 160, group: 'cogs' },

  // ─── COGS indirects ─────────────────────────────────────────────────────
  { section: 'expense', code: 'ICOGS_SERVICES', label: 'Services trafic (indirect)', sort_order: 210, group: 'indirect_cogs' },
  { section: 'expense', code: 'ICOGS_PRODUCTS', label: 'Produits trafic (indirect)', sort_order: 220, group: 'indirect_cogs' },

  // ─── Frais indirects (SG&A) ─────────────────────────────────────────────
  { section: 'expense', code: 'WAGES',        label: 'Salaires & avantages',       sort_order: 310, group: 'opex' },
  { section: 'expense', code: 'OFFICE',       label: 'Bureau & divers',            sort_order: 320, group: 'opex' },
  { section: 'expense', code: 'OCCUPANCY',    label: 'Loyer & occupation',         sort_order: 330, group: 'opex' },
  { section: 'expense', code: 'IT',           label: 'Informatique',               sort_order: 340, group: 'opex' },
  { section: 'expense', code: 'INSURANCE',    label: 'Assurances',                 sort_order: 350, group: 'opex' },
  { section: 'expense', code: 'PROFESSIONAL', label: 'Honoraires professionnels',  sort_order: 360, group: 'opex' },
  { section: 'expense', code: 'TRAVEL',       label: 'Déplacements & représentation', sort_order: 370, group: 'opex' },
  { section: 'expense', code: 'MARKETING',    label: 'Ventes & marketing',         sort_order: 380, group: 'opex' },
  { section: 'expense', code: 'VEHICLE',      label: 'Véhicules',                  sort_order: 390, group: 'opex' },
  { section: 'expense', code: 'OTHER_OPEX',   label: 'Autres frais indirects',     sort_order: 400, group: 'opex' },

  // ─── Autres charges (sous l'EBIT) ───────────────────────────────────────
  { section: 'expense', code: 'DEPRECIATION', label: 'Amortissement',                sort_order: 510, group: 'other' },
  { section: 'expense', code: 'CORP_FEE',     label: 'Frais de gestion corporative', sort_order: 520, group: 'other' },
  { section: 'expense', code: 'INTEREST',     label: 'Intérêts',                     sort_order: 530, group: 'other' },
  { section: 'expense', code: 'TAXES',        label: 'Impôts',                       sort_order: 540, group: 'other' },
  { section: 'expense', code: 'OTHER_EXP',    label: 'Autres charges/(produits)',    sort_order: 550, group: 'other' },
] as const

// Codes de coût des ventes DIRECT (groupe `cogs`) — base de la marge brute,
// cohérente avec le « Gross Profit » du relevé (hors COGS indirects).
export const DIRECT_COGS_CODES: readonly string[] = FINANCE_CATALOG.filter(
  (e) => e.group === 'cogs',
).map((e) => e.code)

// Ensemble des codes connus (revenus + dépenses) — sert au nettoyage des docs
// hors-catalogue (anciennes données de démo) lors de l'import.
export const KNOWN_FINANCE_CODES: ReadonlySet<string> = new Set(
  FINANCE_CATALOG.map((e) => e.code),
)
